import type { AssistantModelMessage, ToolModelMessage } from "ai";
import { z } from "zod";
import { AssistantSchema } from "./Assistant.js";
import { IdentitySchema } from "./Identity.js";
import { SortOrder } from "./Pagination.js";
import { TagMinimalSchema } from "./Tag.js";
import { ToneDefinitionIdSchema } from "./ToneDefinition.js";

export type { AssistantModelMessage, ToolModelMessage } from "ai";

/**
 * TODO: Move this to a common set of tools, to use with Facts and Insights.
 */
export enum ConvoEntryProvenance {
  User = "user",
  Assistant = "assistant",
  UserEditedResult = "user-edited-result",
  CoreTool = "tool",
}

export enum ConvoSource {
  BuildBlockAssistant = "build-block-assistant",
  Build = "build-page",
  CreateAsset = "create-asset",
  CreateAssetAssistant = "create-asset-assistant",
  Ideas = "ideas",
  Dashboard = "dashboard",
  WorkspaceAssistant = "workspace-assistant",
  SalesSummary = "sales-summary",
  SitePerformanceSummary = "site-performance-summary",
  DocumentAnalysisSummary = "document-analysis-summary",
  ExternalChatAssistant = "external-chat",
  TasksBoard = "tasks-board",
  NetworkContact = "network-contact",
  Network = "network",
  Insights = "insights",
}

/**
 * Status to indicate the readiness of a convo (e.g. when used for a document analysis daily summary).
 */
export enum ConvoStatus {
  STARTED = "STARTED",
  FETCHING_DOC = "FETCHING_DOC",
  GENERATING_SUMMARY = "GENERATING_SUMMARY",
  DONE = "DONE",
  FAILED = "FAILED",
}

export const assistantsSources = [
  ConvoSource.ExternalChatAssistant,
  ConvoSource.BuildBlockAssistant,
  ConvoSource.CreateAssetAssistant,
  ConvoSource.Ideas,
  ConvoSource.Dashboard,
] as const;

const ConvoEntryProvenanceSchema = z.object({
  source: z.enum(ConvoEntryProvenance),
  generator: z.string(),
});

export enum ConvoEntryType {
  /**
   * A block of new content, e.g the first message in a convo
   */
  Initial = "initial",

  /**
   * A refinement over a previous entry -- e.g the user edits their original prompt.
   *
   * This entry still includes the full prompt in that case, but knowing it's a refinement
   * we can display it differently, can collapse it, etc.
   */
  Refinement = "refinement",

  /**
   * An chat message entry -- e.g the user adds a new prompt to an existing convo with an assistant.
   */
  Prompt = "prompt",

  /**
   * An entry with a result, which may or may not be the final result in a convo. Generally
   * comes from a generator, but doesn't necesarily have to.
   */
  Result = "result",
}

export const ConvoEntryContentSchema = z.object({
  entryType: z.enum(ConvoEntryType),
  assistantId: AssistantSchema.shape.id.default("general-assistant").optional(),
  provenance: ConvoEntryProvenanceSchema.optional(),

  /**
   * The AI SDK builds its exported schemas on zod's v4 internals, which cannot
   * be composed into the v3 schemas used here -- `z.union` rejects them at
   * runtime. This column is only ever used for its inferred type, so carry the
   * types over directly and skip validation we never run.
   */
  coreMessages: z
    .array(z.custom<AssistantModelMessage | ToolModelMessage>())
    .optional(),

  /**
   * Specifies formatting options for this content - currently we only
   * have text, but later we may want to process facts differently (e.g
   * earnings as specific currency)
   */
  format: z
    .discriminatedUnion("type", [
      // Covers both plain text, and eventually rich text (e.g json markup)
      z.object({
        type: z.literal("text"),
      }),
    ])
    .default({
      type: "text",
    }),

  plainText: z.string().optional(),
});

export const ConvoConfigurationSchema = z.object({
  // Deprecated:
  assistant: z.string().optional(),

  assistantId: AssistantSchema.shape.id.default("general-assistant").optional(),
  /**
   * @deprecated Use tagIds array on the Convo model instead.
   */
  bucket: z.string().optional(),
  isTitleGenerated: z.boolean().optional(),
  // Any source reference to identity from where the chat assistant was originated from (e.g. for a block, the blockPath, for a created asset, the asset convoId, ...)
  sourceRef: z.string().optional(),

  // If defined, affects the tone for this convo; only applies to new entries
  toneDefinitionId: ToneDefinitionIdSchema.optional().nullable(),
  // TODO: Temporary status to handle doc being under analysis. This should go into a new table.
  status: z.string().optional(),
});

export const ConvoEntrySchema = z.object({
  id: z.uuid(),
  content: ConvoEntryContentSchema,
  creatorId: z.uuid().nullable(),
  createdAt: z.date(),
  convoId: z.uuid(),
});

export const ConvoSchema = z.object({
  id: z.uuid(),
  creatorId: z.uuid(),
  organizationId: z.uuid(),
  title: z.string().nullable(),
  private: z.boolean(),
  source: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date().nullish(),
  configuration: ConvoConfigurationSchema,
  externalId: z.string().nullable(),
  tagIds: z.array(z.uuid()),

  /**
   * Computed via an extension (see db/extensions):
   */
  latestGenerator: ConvoEntryContentSchema.shape.provenance
    .unwrap()
    .shape.generator.optional(),
});

export const ConvoCreatorSchema = z.object({
  creator: IdentitySchema.pick({
    email: true,
    id: true,
    name: true,
  }),
});

export const CreateConvoSchema = ConvoSchema.pick({
  configuration: true,
  private: true,
  title: true,
  source: true,
});

export const ConvoWithCreatorSchema = z.object({
  ...ConvoSchema.shape,
  ...ConvoCreatorSchema.shape,
  favoriteId: z.string().optional(),
  tags: z.array(TagMinimalSchema).optional(),
});

export const ConvoWithRelatedSchema = z.object({
  ...ConvoSchema.shape,
  ...ConvoCreatorSchema.shape,
  entries: z.array(ConvoEntrySchema),
  favoriteId: z.string().optional(),
  tags: z.array(TagMinimalSchema).optional(),
});

export const CreateConvoEntrySchema = z.object({
  content: ConvoEntrySchema.shape.content,
  convoId: ConvoEntrySchema.shape.convoId.nullable(),
  convoSource: ConvoSchema.shape.source.optional(),
  sourceRef: z.string().optional(),
});

export type ConvoSortField = keyof typeof ConvoSchema.shape;

export const sortFields = Object.keys(ConvoSchema.shape) as [ConvoSortField];

export const FilterConvoSchema = z.object({
  createdBy: z.union([z.literal("self"), z.uuid()]).optional(),
  /**
   * @deprecated Use tagIds for filtering.
   */
  inBucket: z.string().optional(),
  /**
   * Filter by tag IDs. If multiple IDs provided, returns convos that have ANY of the tags.
   */
  tagIds: z.array(z.uuid()).optional(),
  source: z.array(z.enum(ConvoSource)).optional(),
  sourceRef: z.string().max(128).optional(),
  includeActiveEntry: z.boolean().optional(),
  skip: z.int().optional(),
  take: z.int().max(100).optional(),
  orderBy: z
    .object({
      sort: z.enum(sortFields),
      order: z.enum(SortOrder),
    })
    .optional(),
});

export const ConvoPromptSuggestionsSchema = z.object({
  ideas: z.array(z.string()),
});

export type CreateConvoEntry = z.infer<typeof CreateConvoEntrySchema>;
export type CreateConvo = z.infer<typeof CreateConvoSchema>;
export type ConvoConfiguration = z.infer<typeof ConvoConfigurationSchema>;
export type ConvoEntryContent = z.infer<typeof ConvoEntryContentSchema>;
export type Convo = z.infer<typeof ConvoSchema>;
export type ConvoWithCreator = z.infer<typeof ConvoWithCreatorSchema>;
export type ConvoWithRelated = z.infer<typeof ConvoWithRelatedSchema>;
export type ConvoEntry = z.infer<typeof ConvoEntrySchema>;
export type FilterConvoParams = z.infer<typeof FilterConvoSchema>;
export type ConvoPromptSuggestions = z.infer<
  typeof ConvoPromptSuggestionsSchema
>;

export function getActiveEntry(
  convo: ConvoWithRelated,
  { sort = SortOrder.ASC }: { sort?: SortOrder } = {},
) {
  const refinementOrInitialPromptEntries = convo.entries.filter(
    (e) =>
      e.content.entryType === ConvoEntryType.Initial ||
      e.content.entryType === ConvoEntryType.Refinement,
  );

  const resultEntries = convo.entries.filter(
    (e) => e.content.entryType === ConvoEntryType.Result,
  );

  const position = sort === "desc" ? 0 : -1;
  const activePromptEntry = refinementOrInitialPromptEntries.at(position);
  const activeResultEntry = resultEntries.at(position);

  return {
    prompt: activePromptEntry,
    result: activeResultEntry,
  };
}
